/**
 * External Partners API Service - Company Hierarchy Version
 *
 * Manages partner companies and their Cloud Identity users.
 */

import api from './api';

// =============================================================================
// TYPES
// =============================================================================

export interface PartnerCompany {
  id: string;
  name: string;
  slug: string;
  googleOuPath: string | null;
  googleOuId: string | null;
  description: string | null;
  primaryContact: string | null;
  isActive: boolean;
  userCount: number;
  activeUserCount: number;
  pendingUserCount: number;
  suspendedUserCount: number;
  createdAt: string;
  createdBy: {
    id: string;
    name: string;
    email: string;
  };
}

export interface PartnerUser {
  id: string;
  partnerId: string;
  partnerName: string;
  googleUserId: string | null;
  email: string;
  firstName: string;
  lastName: string;
  fullName: string;
  externalEmail: string | null;
  status: 'Pending' | 'Active' | 'Suspended';
  inviteSentAt: string | null;
  activatedAt: string | null;
  suspendedAt: string | null;
  suspendedReason: string | null;
  createdAt: string;
  createdBy: {
    id: string;
    name: string;
    email: string;
  };
  // App User link (for permissions/client access editing)
  appUserId?: string;
  role?: 'Admin' | 'Staff';
  clientIds?: string[];
}

export interface ExternalPartnerStatus {
  configured: boolean;
  connected: boolean;
  ouExists: boolean;
  companyCount: number;
  totalUserCount: number;
  error?: string;
}

export interface CreateCompanyInput {
  name: string;
  description?: string;
  primaryContact?: string;
}

export interface UpdateCompanyInput {
  name?: string;
  description?: string | null;
  primaryContact?: string | null;
  isActive?: boolean;
}

export interface CreateUserInput {
  firstName: string;
  lastName: string;
  externalEmail?: string;
  sendWelcomeEmail?: boolean;
  role?: 'Admin' | 'Staff';
  clientIds?: string[];
  permissionOverrides?: Array<{ permissionId: string; granted: boolean; reason?: string }>;
}

export interface UpdateUserInput {
  firstName?: string;
  lastName?: string;
  externalEmail?: string | null;
}

export interface CreateUserResult {
  user: PartnerUser;
  credentialLink: string;
  credentialExpiresAt: string;
  message: string;
}

export interface BulkCreateUsersInput {
  users: Array<{ firstName: string; lastName: string; externalEmail?: string }>;
  role?: 'Admin' | 'Staff';
  clientIds?: string[];
  permissionOverrides?: Array<{ permissionId: string; granted: boolean; reason?: string }>;
  sendWelcomeEmail?: boolean;
}

export interface BulkCreateUsersResult {
  results: Array<{
    status: 'created' | 'failed';
    user?: PartnerUser;
    credentialLink?: string;
    credentialExpiresAt?: string;
    input?: { firstName: string; lastName: string; externalEmail?: string };
    error?: string;
  }>;
}

export interface SyncResult {
  companiesImported: number;
  companiesSkipped: number;
  usersSynced: number;
  errors: string[];
}

// =============================================================================
// STATUS & SYNC
// =============================================================================

/**
 * Get external partner service status
 */
export const getExternalPartnerStatus = async (): Promise<ExternalPartnerStatus> => {
  const { data } = await api.get<ExternalPartnerStatus>('/external-partners/status');
  return data.data;
};

/**
 * Sync users from Google to database
 */
export const syncFromGoogle = async (): Promise<SyncResult> => {
  const response = await api.client.post<{ data: SyncResult }>('/external-partners/sync');
  return response.data.data;
};

// =============================================================================
// COMPANY MANAGEMENT
// =============================================================================

/**
 * List all partner companies
 */
export const listPartnerCompanies = async (): Promise<PartnerCompany[]> => {
  const { data } = await api.get<PartnerCompany[]>('/external-partners');
  return data.data;
};

/**
 * Get a single partner company by ID
 */
export const getPartnerCompany = async (partnerId: string): Promise<PartnerCompany | null> => {
  const { data } = await api.get<PartnerCompany | null>(`/external-partners/${partnerId}`);
  return data.data;
};

/**
 * Create a new partner company
 */
export const createPartnerCompany = async (input: CreateCompanyInput): Promise<PartnerCompany> => {
  const response = await api.client.post<{ data: PartnerCompany }>('/external-partners', input);
  return response.data.data;
};

/**
 * Update a partner company
 */
export const updatePartnerCompany = async (
  partnerId: string,
  updates: UpdateCompanyInput
): Promise<PartnerCompany> => {
  const { data } = await api.put<PartnerCompany>(`/external-partners/${partnerId}`, updates);
  return data.data;
};

/**
 * Archive a partner company
 */
export const archivePartnerCompany = async (partnerId: string): Promise<{ message: string }> => {
  const response = await api.client.post<{ message: string }>(`/external-partners/${partnerId}/archive`);
  return { message: response.data.message };
};

/**
 * Unarchive (restore) a partner company
 */
export const unarchivePartnerCompany = async (partnerId: string): Promise<{ message: string }> => {
  const response = await api.client.post<{ message: string }>(`/external-partners/${partnerId}/unarchive`);
  return { message: response.data.message };
};

// =============================================================================
// USER MANAGEMENT (within company)
// =============================================================================

/**
 * List users in a partner company
 */
export const listPartnerUsers = async (partnerId: string): Promise<PartnerUser[]> => {
  const { data } = await api.get<PartnerUser[]>(`/external-partners/${partnerId}/users`);
  return data.data;
};

/**
 * Get a single partner user
 */
export const getPartnerUser = async (
  partnerId: string,
  userId: string
): Promise<PartnerUser | null> => {
  const { data } = await api.get<PartnerUser | null>(`/external-partners/${partnerId}/users/${userId}`);
  return data.data;
};

/**
 * Create a user in a partner company
 */
export const createPartnerUser = async (
  partnerId: string,
  input: CreateUserInput
): Promise<CreateUserResult> => {
  const response = await api.client.post<{
    data: PartnerUser;
    credentialLink: string;
    credentialExpiresAt: string;
    message: string;
  }>(`/external-partners/${partnerId}/users`, input);
  return {
    user: response.data.data,
    credentialLink: response.data.credentialLink,
    credentialExpiresAt: response.data.credentialExpiresAt,
    message: response.data.message,
  };
};

/**
 * Create multiple users in a partner company
 */
export const createPartnerUsers = async (
  partnerId: string,
  input: BulkCreateUsersInput
): Promise<BulkCreateUsersResult> => {
  const response = await api.client.post<{ data: BulkCreateUsersResult }>(
    `/external-partners/${partnerId}/users/bulk`,
    input
  );
  return response.data.data;
};

/**
 * Update a partner user
 */
export const updatePartnerUser = async (
  partnerId: string,
  userId: string,
  updates: UpdateUserInput
): Promise<PartnerUser> => {
  const { data } = await api.put<PartnerUser>(
    `/external-partners/${partnerId}/users/${userId}`,
    updates
  );
  return data.data;
};

/**
 * Reset password for a partner user
 */
export const resetPartnerUserPassword = async (
  partnerId: string,
  userId: string,
  sendEmail: boolean = true
): Promise<{ credentialLink: string; credentialExpiresAt: string; message: string }> => {
  const response = await api.client.post<{ credentialLink: string; credentialExpiresAt: string; message: string }>(
    `/external-partners/${partnerId}/users/${userId}/reset-password`,
    { sendEmail }
  );
  return {
    credentialLink: response.data.credentialLink,
    credentialExpiresAt: response.data.credentialExpiresAt,
    message: response.data.message,
  };
};

/**
 * Suspend a partner user
 */
export const suspendPartnerUser = async (
  partnerId: string,
  userId: string,
  reason?: string
): Promise<{ message: string }> => {
  const response = await api.client.post<{ message: string }>(
    `/external-partners/${partnerId}/users/${userId}/suspend`,
    { reason }
  );
  return { message: response.data.message };
};

/**
 * Unsuspend a partner user
 */
export const unsuspendPartnerUser = async (
  partnerId: string,
  userId: string
): Promise<{ message: string }> => {
  const response = await api.client.post<{ message: string }>(
    `/external-partners/${partnerId}/users/${userId}/unsuspend`
  );
  return { message: response.data.message };
};

/**
 * Delete a partner user (permanent!)
 */
export const deletePartnerUser = async (
  partnerId: string,
  userId: string
): Promise<{ message: string }> => {
  const response = await api.client.delete<{ message: string }>(
    `/external-partners/${partnerId}/users/${userId}`,
    { data: { confirm: true } }
  );
  return { message: response.data.message };
};
